import {
    format,
    addDays,
    addHours,
    subDays,
    subHours,
    startOfHour,
    isWithinInterval,
    parseISO
} from 'date-fns';
import { cphHeaders } from './cphRequest';

let cachedData = {};

const applySearch = (flights, search) => {
    if (!search) return flights;
    const searchLower = search.toLowerCase();
    return flights.filter((flight) => {
        return (
            flight.Destination.toLowerCase().includes(searchLower) ||
            flight.Gate.toLowerCase().includes(searchLower) ||
            flight.Iata.toLowerCase().includes(searchLower) ||
            flight.Airline.toLowerCase().includes(searchLower) ||
            flight.Status.toLowerCase().includes(searchLower)
        );
    });
};

// Fetch + sort flights from the CPH API for the given window.
const fetchFlightsFromApi = async (direction, defaultFrom, defaultTo) => {
    // For direction we can do A for arrivals and D for departures

    // we modify dates slightly to get us the same as cph website
    let fromDate = subDays(defaultFrom, 1);
    fromDate = subHours(fromDate, 2);
    fromDate = startOfHour(fromDate);
    const modifiedFrom = format(fromDate, "yyyy-MM-dd'T'HH:00:00.000") + 'Z';

    let toDate = addDays(defaultTo, 1);
    toDate = subHours(toDate, 2); // to fix locale iso
    toDate = startOfHour(toDate);
    const modifiedTo = format(toDate, "yyyy-MM-dd'T'HH:00:00.000") + 'Z';
    const url = `https://www.cph.dk/api/FlightInformation/GetFlightInfoTable?direction=${direction}&userQuery=*:*&startDateTime=${modifiedFrom}&endDateTime=${modifiedTo}&language=da`;

    const retryFetch = async (url, options, maxRetries = 3) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const response = await fetch(url, options);
                if (!response.ok) {
                    throw new Error(
                        `HTTP ${response.status}: ${response.statusText}`
                    );
                }
                return response;
            } catch (error) {
                console.error(`Flight API attempt ${attempt} failed:`, {
                    url,
                    message: error.message,
                    cause: error.cause,
                    code: error.code,
                    attempt,
                    maxRetries
                });

                if (attempt === maxRetries) {
                    throw error;
                }

                // Exponential backoff: 1s, 2s, 4s
                const delay = Math.pow(2, attempt - 1) * 1000;
                console.log(`Retrying flight API in ${delay}ms...`);
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }
    };

    const response = await retryFetch(url, {
        headers: cphHeaders({ Accept: 'application/json' }),
        signal: AbortSignal.timeout(60000) // 60 second timeout
    });

    const flights = await response.json();

    // sort by ExpectedDateTime
    flights.sort((a, b) => {
        const dateA = parseISO(a.ExpectedDateTime);
        const dateB = parseISO(b.ExpectedDateTime);
        return dateA - dateB;
    });

    return flights;
};

export const getFlightData = async ({ direction, from, to, search }) => {
    const fromOrToPassedToFunction = Boolean(from || to);
    const defaultFrom = from || new Date();
    const defaultTo = to || new Date();

    // Custom window: always a fresh fetch, never cached.
    if (fromOrToPassedToFunction) {
        const flights = await fetchFlightsFromApi(
            direction,
            defaultFrom,
            defaultTo
        );
        return applySearch(flights, search);
    }

    // Default window: cache per direction AND de-duplicate concurrent fetches
    // via an in-flight promise, so multiple callers in the same tick (e.g.
    // getDelayedFlightData + getDepartureSession in Promise.all) share ONE CPH
    // API request instead of each racing past the cache check and firing their
    // own.
    const ttl = process.env.NODE_ENV === 'development' ? 11000 : 1 * 60 * 1000;
    const entry = cachedData[direction];

    if (
        entry?.flights &&
        entry?.lastFetchTime &&
        new Date() - entry.lastFetchTime < ttl
    ) {
        return applySearch(entry.flights, search);
    }

    if (entry?.inFlight) {
        const flights = await entry.inFlight;
        return applySearch(flights, search);
    }

    const inFlight = fetchFlightsFromApi(direction, defaultFrom, defaultTo);
    cachedData[direction] = { ...entry, inFlight };

    try {
        const flights = await inFlight;
        cachedData[direction] = { flights, lastFetchTime: new Date() };
        return applySearch(flights, search);
    } catch (error) {
        // Drop the failed in-flight promise so the next call retries.
        if (cachedData[direction]?.inFlight === inFlight) {
            delete cachedData[direction].inFlight;
        }
        throw error;
    }
};

// A gap of at least this long between consecutive departures marks the
// overnight break that separates one operating "session" from the next.
const NIGHT_GAP_MS = 3 * 60 * 60 * 1000;

/**
 * Find the first and last departure of the relevant operating SESSION - not the
 * calendar day. Departures are grouped into sessions split by the overnight
 * no-fly gap, so a 00:10 red-eye is treated as the tail of the previous evening
 * rather than "today's first departure". The relevant session is the current or
 * next one (the first whose last departure is still ahead of `now`), which is
 * what determines when security opens.
 *
 * Shares getFlightData's request with the concurrent delayed-flights fetch:
 * getFlightData de-duplicates same-tick callers via an in-flight promise, so
 * this adds no extra CPH API call. getFlightData returns ~26h back to ~24h
 * ahead, which is plenty to bracket the current/next session.
 */
export const getDepartureSession = async ({ now = new Date() } = {}) => {
    try {
        const flights = await getFlightData({ direction: 'D' });

        const deps = flights
            .filter((f) => f.ScheduledDateTime)
            .map((f) => ({ f, sched: parseISO(f.ScheduledDateTime) }))
            .filter(({ sched }) => !isNaN(sched))
            .sort((a, b) => a.sched - b.sched);

        if (deps.length === 0) {
            return {
                firstDeparture: null,
                firstFlightLabel: null,
                lastDeparture: null
            };
        }

        // Split into operating sessions wherever there is an overnight-sized gap.
        const sessions = [];
        let current = [deps[0]];
        for (let i = 1; i < deps.length; i++) {
            if (deps[i].sched - deps[i - 1].sched >= NIGHT_GAP_MS) {
                sessions.push(current);
                current = [];
            }
            current.push(deps[i]);
        }
        sessions.push(current);

        // Relevant session = current one (now within it) or the next one:
        // the first session whose LAST departure is still at/after now.
        const session =
            sessions.find((s) => s[s.length - 1].sched >= now) ||
            sessions[sessions.length - 1];

        const first = session[0];
        const last = session[session.length - 1];
        const firstFlightLabel = [first.f.Iata, first.f.Destination]
            .filter(Boolean)
            .join(' → ');

        return {
            firstDeparture: first.sched,
            firstFlightLabel,
            lastDeparture: last.sched
        };
    } catch (error) {
        console.error('getDepartureSession failed:', error.message);
        return {
            firstDeparture: null,
            firstFlightLabel: null,
            lastDeparture: null
        };
    }
};

export const getDelayedFlightData = async ({ direction, from, to } = {}) => {
    const flights = await getFlightData({ direction, from, to });

    // Statuses that indicate the flight has already departed/landed
    const completedStatuses = [
        'closed',
        'departed',
        'landed',
        'baggage',
        'arrived'
    ];

    // filter delayed flights
    const currentTime = new Date();
    const twelveHoursFromNow = addHours(currentTime, 12);

    // Filter the flights based on conditions
    const filteredFlights = flights.filter((flight) => {
        if (!flight.Delayed) return false;

        // Skip flights that have already departed/completed
        if (completedStatuses.includes(flight.Status?.toLowerCase())) {
            return false;
        }

        // Parse the ISO datetime string
        const expectedDateTime = parseISO(flight.ExpectedDateTime);

        // Check if the expected time is within the next 12 hours
        return isWithinInterval(expectedDateTime, {
            start: currentTime,
            end: twelveHoursFromNow
        });
    });
    return filteredFlights;
};
